import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';

import ActiveCall from 'models/active_call';
import AgentProfile from 'models/agent_profile';
import AutoAcceptedSessionNotification from './auto_accepted_session_notification';
import ConversationNotification from './conversation_notification';
import DeclinedSessionReassignmentNotification from './declined_session_reassignment_notification';
import DisconnectedNotification from './disconnected_notification';
import DismissNotificationButtonContainer from './dismiss_notification_button';
import ErrorNotification from './error_notification';
import IncomingCallNotificationContainer from './incoming_call_notification';
import IncomingConcurrentMessageNotification from 'components/notification/incoming_concurrent_message_notification';
import IncomingSessionNotification from './incoming_session_notification';
import Notification from './notification';
import NotificationClasses from 'models/notification/notification_classes';
import NotificationType from 'models/notification/notification_type';
import OptionalReloadNotificationContainer from './optional_reload_notification';
import OptionalUpgradeNotificationContainer from './optional_upgrade_notification';
import TaskNotification from './task_notification';
import UnofferedWarmTransferNotification from './unoffered_warm_transfer_notification';
import { typeFromNotification } from 'scripts/adapters/immutable_converters/notification_converter';
import { UnofferedCallMessages } from 'models/routing_event/unoffered_call_type';

function Notifications({ activeCall, currentAgent, onDismissNotification, notifications }) {
  let hasActiveCall = activeCall && activeCall.shouldDisplayIncomingCallNotification(currentAgent && currentAgent.id);
  return (
    <div className="notificationList">
      {hasActiveCall && <IncomingCallNotificationContainer />}
      <IncomingSessionNotification />
      {notifications.length > 0 && _.map(notifications, notification => renderNotification(notification))}
    </div>
  );

  function renderNotification(notification) {
    const notificationType = typeFromNotification(notification);
    switch (notificationType) {
      case NotificationType.ADMIN:
        return renderAdminNotification();
      case NotificationType.ASSIGNED_CONVERSATION:
        return renderConversationNotification();
      case NotificationType.ASSIGNED_TASK:
        return renderTaskNotification();
      case NotificationType.AUTO_ACCEPTED_SESSION_NOTIFICATION:
        return renderAutoAcceptedSessionNotification();
      case NotificationType.DECLINED_REASSIGNED_SESSION:
        return renderDeclinedSessionReassignmentNotification();
      case NotificationType.DISCONNECT:
        return renderUnstableConnectionNotification();
      case NotificationType.DUE_TASK:
        return renderTaskNotification();
      case NotificationType.ERROR:
        return renderErrorNotification();
      case NotificationType.INCOMING_CONCURRENT_MESSAGE:
        return renderIncomingConcurrentMessageNotification();
      case NotificationType.OPTIONAL_RELOAD:
        return renderOptionalReloadNotificaiton();
      case NotificationType.OPTIONAL_UPGRADE:
        return renderOptionalUpgradeNotification();
      case NotificationType.SUCCESS:
        return renderSuccessNotification();
      case NotificationType.UNOFFERED_CALL:
        return renderUnofferedCallNotification();
      case NotificationType.UNOFFERED_WARM_TRANSFER:
        return renderUnofferedWarmTransferNotification();
      default:
        return null;
    }

    function renderUnstableConnectionNotification() {
      return (
        <DisconnectedNotification
          key={`notification-${notification.id}`}
          id={notification.id}
          onDismiss={onDismissNotification}
        />
      );
    }

    function renderAdminNotification() {
      return (
        <Notification
          contentClasses="adminNotification"
          key={`notification-${notification.id}`}
          notificationId={notification.id}
          onClick={onDismissNotification}
        >
          <div className="adminNotification-message">{notification.message}</div>
          <DismissNotificationButtonContainer notificationId={notification.id} />
        </Notification>
      );
    }

    function renderAutoAcceptedSessionNotification() {
      return <AutoAcceptedSessionNotification key={`notification-${notification.id}`} notification={notification} />;
    }

    function renderConversationNotification() {
      return (
        <ConversationNotification
          currentAgent={currentAgent}
          key={`notification-${notification.id}`}
          notification={notification}
        />
      );
    }

    function renderTaskNotification() {
      return <TaskNotification notification={notification} key={`notification-${notification.id}`} />;
    }

    function renderErrorNotification() {
      return <ErrorNotification notification={notification} key={`notification-${notification.id}`} />;
    }

    function renderDeclinedSessionReassignmentNotification() {
      return (
        <DeclinedSessionReassignmentNotification notification={notification} key={`notification-${notification.id}`} />
      );
    }

    function renderIncomingConcurrentMessageNotification() {
      return (
        <IncomingConcurrentMessageNotification notification={notification} key={`notification-${notification.id}`} />
      );
    }

    function renderOptionalReloadNotificaiton() {
      return activeCall ? null : (
        <OptionalReloadNotificationContainer key={`notification-${notification.id}`} notification={notification} />
      );
    }

    function renderOptionalUpgradeNotification() {
      return notification.isPostponed || activeCall ? null : (
        <OptionalUpgradeNotificationContainer
          key={`notification-${notification.id}`}
          notificationId={notification.id}
        />
      );
    }

    function renderUnofferedCallNotification() {
      return (
        <Notification
          key={`notification-${notification.id}`}
          contentClasses="unofferedCallNotification"
          notificationId={notification.id}
        >
          <div className="unofferedCallNotification-message">{UnofferedCallMessages[notification.reason]}</div>
          <DismissNotificationButtonContainer notificationId={notification.id} />
        </Notification>
      );
    }

    function renderUnofferedWarmTransferNotification() {
      return <UnofferedWarmTransferNotification key={`notification-${notification.id}`} notification={notification} />;
    }

    function renderSuccessNotification() {
      return (
        <Notification
          data-aid="success-notification"
          key={`notification-${notification.id}`}
          contentClasses="successNotification"
          onClick={onDismissNotification}
          notificationId={notification.id}
        >
          <i className="successNotification-icon" />
          <div data-aid="successNotification-message">{notification.message}</div>
        </Notification>
      );
    }
  }
}

Notifications.propTypes = {
  activeCall: PropTypes.instanceOf(ActiveCall),
  currentAgent: PropTypes.instanceOf(AgentProfile),
  notifications: PropTypes.arrayOf(PropTypes.oneOfType(_.map(NotificationClasses, c => PropTypes.instanceOf(c[1]))))
    .isRequired,
  onDismissNotification: PropTypes.func.isRequired,
};

export default Notifications;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/notification/notifications.jsx